import forge from 'apis/forge';
import { Scene } from 'dragonmouse-shared/interfaces';
import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { sceneShareUrl } from 'utils';
import ShareEntityModal, { ShareEntityModalConfig } from '../ShareEntityModal';

interface ShareSceneModalProps {
	scene: Scene;
	onClose: () => void;
}

const ShareSceneModal = ({ scene, onClose }: ShareSceneModalProps) => {
	const [publiclyReadable, setPubliclyReadable] = useState(!!scene?.metadata?.publiclyReadable);

	const { isMobile, user } = useSelector((r: RootReducer) => {
		return {
			isMobile: selectIsMobile(r.mode),
			user: selectUser(r.db),
		};
	}, shallowEqual);
	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const onAccessChange = useCallback(
		async (newPubliclyReadable: boolean) => {
			try {
				await forge.updateScene(
					{
						update: {
							id: scene.id,
							metadata: {
								...scene.metadata,
								publiclyReadable: newPubliclyReadable,
							},
						},
					},
					true
				);
				toast.success('Project access updated');
				setPubliclyReadable(newPubliclyReadable);
			} catch (error) {
				toast.error('Failed to update project access settings');
				console.error('Error updating scene access:', error);
			}
		},
		[scene]
	);

	const config: ShareEntityModalConfig = useMemo(
		() => ({
			title: 'Share this project',
			entityLabel: 'project',
			publiclyReadable,
			showPrivateOption: canEdit(scene),
			isPubliclyReadableForced: !!scene.viewOnly,
			canManageAccess: canEdit(scene),
			shareUrl: sceneShareUrl(scene),
			shareTitle: scene.title ?? 'Moonchild project',
			canNativeShare,
			trackAction: canNativeShare ? 'share_scene_link' : 'copy_scene_link',
			onAccessChange,
			onClose: close,
			accessManagerProps: {
				title: 'Manage Project Editors',
				onClose: close,
				copyLink: {
					shareUrl: sceneShareUrl(scene),
					linkLabel: canNativeShare ? 'Share project link' : 'Copy project link',
					canNativeShare,
					shareTitle: scene.title ?? 'Moonchild project',
					trackAction: canNativeShare ? 'share_scene_link' : 'copy_scene_link',
				},
				userAccess: {
					entityId: scene.id,
					getUsers: async (id) => forge.getSceneUsers({ sceneId: id }),
					grantAccess: async (id, emails) =>
						forge.grantSceneAccess.withToast()({ sceneId: id, emails }),
					revokeAccess: async (id, email) =>
						forge.revokeSceneAccess.withToast()({ sceneId: id, email }),
					errorMessages: {
						fetchFailed: 'Failed to fetch scene users',
						revokeFailed: 'Failed to revoke user access',
					},
					onAccessGranted: () => {
						if (user?.id) {
							forge.updateUser({
								update: {
									id: user.id,
									metadata: { lastSharedDate: new Date().toISOString() },
								},
							});
						}
					},
				},
				organizationAccess: {
					entityId: scene.id,
					getOrganizations: (id) => forge.getSceneOrganizations(id),
					addToOrganization: (entityId, orgId) =>
						forge.addSceneToOrganization.withToast()(entityId, orgId),
					removeFromOrganization: (entityId, orgId) =>
						forge.removeSceneFromOrganization.withToast()(entityId, orgId),
					emptyStateMessage: 'This project is not shared with any teams',
					fetchFailedMessage: 'Failed to load scene teams',
				},
			},
		}),
		[publiclyReadable, scene, canNativeShare, onAccessChange, close, user?.id]
	);

	return <ShareEntityModal config={config} />;
};

export default ShareSceneModal;
